// src/firebase/services.js
// Matches real Firestore: categories / subcategories / vendors / restaurantitems
import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc,
  deleteDoc, query, where, orderBy, serverTimestamp,
  setDoc, writeBatch
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, updateProfile, GoogleAuthProvider, signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from './config';

// ─────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────
export const registerUser = async (email, password, displayName) => {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName });
  await setDoc(doc(db, 'users', cred.user.uid), {
    uid: cred.user.uid, email, displayName,
    role: 'customer', createdAt: serverTimestamp(),
    wishlist: [], orders: [], phone: '',
    loginStatus: 'web',
    registeredFrom: 'web',
    lastLoginAt: serverTimestamp(),
  });
  return cred.user;
};

export const loginUser = async (email, password) => {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  await updateDoc(doc(db, 'users', cred.user.uid), {
    loginStatus: 'web',
    lastLoginAt: serverTimestamp(),
  });
  return cred;
};

export const loginWithGoogle = async () => {
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  const userRef = doc(db, 'users', result.user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(userRef, {
      uid: result.user.uid, email: result.user.email,
      displayName: result.user.displayName,
      role: 'customer', createdAt: serverTimestamp(),
      wishlist: [], orders: [], phone: '',
      loginStatus: 'web',
      registeredFrom: 'web',
      lastLoginAt: serverTimestamp(),
    });
  } else {
    await updateDoc(userRef, {
      loginStatus: 'web',
      lastLoginAt: serverTimestamp(),
    });
  }
  return result.user;
};

export const logoutUser = () => signOut(auth);
export const resetPassword = (email) => sendPasswordResetEmail(auth, email);

// Check if email already registered.
// Strategy: attempt createUserWithEmailAndPassword with a temp password.
// - auth/email-already-in-use → exists (password account)
// - auth/operation-not-allowed → treat as unknown (don't block)
// - If it SUCCEEDS (new user created) → immediately delete, return free
// This works even when Email Enumeration Protection is ON in Firebase console.
export const checkEmailExists = async (email) => {
  const DUMMY_PW = `_chk_${Math.random().toString(36).slice(2)}Aa1!`;
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, DUMMY_PW);
    // Email was FREE — a new account was just created. Delete it immediately.
    await cred.user.delete();
    return { exists: false, providers: [] };
  } catch (err) {
    const code = err.code || '';
    if (code === 'auth/email-already-in-use') {
      return { exists: true, providers: ['password'] };
    }
    if (code === 'auth/invalid-email') {
      return { exists: false, providers: [] };
    }
    if (code === 'auth/too-many-requests') {
      return { exists: null, providers: [] };
    }
    // auth/operation-not-allowed or any other — fail silently, don't block
    return { exists: null, providers: [] };
  }
};

export const updateUserProfile = async (uid, data) => {
  await updateDoc(doc(db, 'users', uid), data);
  if (data.displayName && auth.currentUser)
    await updateProfile(auth.currentUser, { displayName: data.displayName });
};

// ─────────────────────────────────────────
// USERS
// ─────────────────────────────────────────
export const getUser = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};
export const getAllUsers = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
export const updateUserRole = (uid, role) => updateDoc(doc(db, 'users', uid), { role });

// ─────────────────────────────────────────
// CATEGORIES
// ─────────────────────────────────────────
export const getActiveCategories = async () => {
  const snap = await getDocs(collection(db, 'category'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(c => c.active === true || c.isActive === true)
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};
export const getCategoryById = async (id) => {
  const snap = await getDoc(doc(db, 'category', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// ─────────────────────────────────────────
// SUBCATEGORIES
// ─────────────────────────────────────────
export const getSubcategoriesByCategoryId = async (categoryId) => {
  const snap = await getDocs(collection(db, 'subcategory'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(s =>
      (s.categoryId === categoryId || s.mainCategoryId === categoryId) &&
      s.active !== false
    )
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};
export const getSubcategoryById = async (id) => {
  const snap = await getDoc(doc(db, 'subcategory', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};
export async function getSubcategoriesByVendorId(vendorId) {
  const q = query(
    collection(db, 'subcategories'),
    where('vendorId', '==', vendorId),
    where('active', '==', true)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─────────────────────────────────────────
// VENDOR CATEGORIES (vendor_categories collection)
// ─────────────────────────────────────────
export const getVendorCategories = async (vendorId) => {
  const snap = await getDocs(collection(db, 'vendor_categories'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(c => c.vendorId === vendorId && (c.active === true || c.activetrue === true))
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};

// ─────────────────────────────────────────
// VENDORS
// ─────────────────────────────────────────
export const getVendorsByCategoryId = async (categoryId) => {
  const snap = await getDocs(collection(db, 'vendors'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(v => v.categoryId === categoryId && v.isActive === true)
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};
export const getVendorsBySubcategoryId = async (subCategoryId) => {
  const snap = await getDocs(collection(db, 'vendors'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(v =>
      v.isActive === true &&
      Array.isArray(v.subCategoryIds) &&
      v.subCategoryIds.includes(subCategoryId)
    )
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};
export const getVendorById = async (id) => {
  const snap = await getDoc(doc(db, 'vendors', id));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (data.isActive !== true) return null;
  return { id: snap.id, ...data };
};
export const getAllVendors = async () => {
  const snap = await getDocs(collection(db, 'vendors'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(v => v.isActive === true)
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};

// ─────────────────────────────────────────
// RESTAURANT ITEMS
// ─────────────────────────────────────────
export const getItemsByVendorId = async (vendorId) => {
  const snap = await getDocs(collection(db, 'restaurantitems'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(item => item.VendorId === vendorId && item.Active === true)
    .sort((a, b) => (a.ItemName || '').localeCompare(b.ItemName || ''));
};
export const getItemsBySubcategoryAndVendor = async (vendorId, subCategoryId) => {
  const snap = await getDocs(collection(db, 'restaurantitems'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(item =>
      item.VendorId === vendorId &&
      item.SubCategoryId === subCategoryId &&
      item.Active === true
    );
};
export const getItemsByCategoryId = async (categoryId) => {
  const snap = await getDocs(collection(db, 'restaurantitems'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(item => item.MainCategoryId === categoryId && item.Active === true);
};

// ─────────────────────────────────────────
// PRODUCTS (mapped from restaurantitems)
// ─────────────────────────────────────────
export const getProducts = async () => {
  const snap = await getDocs(collection(db, 'restaurantitems'));
  return snap.docs.map(d => ({
    id: d.id,
    name: d.data().ItemName,
    price: d.data().ItemPrice || d.data().TotalPrice,
    image: d.data().ImageBlob,
    vendorId: d.data().VendorId,
    subCategoryId: d.data().SubCategoryId,
    active: d.data().Active,
    ...d.data(),
  }));
};
export const getProduct = async (id) => {
  const snap = await getDoc(doc(db, 'restaurantitems', id));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    name: data.ItemName,
    price: data.ItemPrice || data.TotalPrice,
    image: data.ImageBlob,
    ...data,
  };
};
export const addProduct = async (data) => {
  const ref = await addDoc(collection(db, 'restaurantitems'), {
    ItemName: data.name,
    ItemPrice: data.price,
    TotalPrice: data.price,
    ImageBlob: data.image || '',
    VendorId: data.vendorId || '',
    SubCategoryId: data.subCategoryId || '',
    Active: true,
    createdAt: serverTimestamp(),
  });
  return ref.id;
};
export const updateProduct = async (id, data) => {
  await updateDoc(doc(db, 'restaurantitems', id), {
    ItemName: data.name,
    ItemPrice: data.price,
    TotalPrice: data.price,
    ImageBlob: data.image,
    updatedAt: serverTimestamp(),
  });
};
export const deleteProduct = async (id) => {
  await deleteDoc(doc(db, 'restaurantitems', id));
};

// ─────────────────────────────────────────
// ORDERS
// ─────────────────────────────────────────
export const createOrder = async (userId, orderData) => {
  const batch    = writeBatch(db);
  const orderRef = doc(collection(db, 'orders'));
  const docId    = orderRef.id;
  const now      = new Date();

  const isCOD  = orderData.paymentMethod === 'COD' || orderData.paymentMethod === 'cod';

  // Order ID format:
  //   COD    →  COD_<timestamp><random4>    e.g. COD_17432859230001
  //   Online →  Razorpay payment ID         e.g. PAY_SMLYAX8GNRZUXK
  const orderId = isCOD
    ? `COD_${String(now.getTime()).slice(-13)}`   // exactly 13 digits
    : (orderData.OrderId || `PAY_${Math.random().toString(36).substr(2, 16).toUpperCase()}`);

  const firstItem  = (orderData.orderDetails || [])[0] || {};
  const vendorId   = orderData.vendorId   || firstItem.vendorId   || '';
  const vendorName = orderData.vendorName || firstItem.vendorName || '';

  const initialStatus = 'Created';
  const initialMain   = 'Order Placed';
  const description   = `Order placed for ₹${(orderData.totalAmount || 0).toFixed(2)}`;

  const orderDoc = {
    // ── IDs ──
    FirestoreDocId: docId,
    OrderId:        orderId,
    userId,

    // ── Source ──
    orderStatus: 'Web',          // ← marks this order came from the web app

    // ── Address ──
    address: orderData.address || {},

    // ── Coupon ──
    couponCode: orderData.couponCode || '',
    couponid:   orderData.couponid   || 0,

    // ── Timestamps ──
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),

    // ── Customer ──
    customerName: orderData.customerName || '',
    phoneNumber:  orderData.phoneNumber  || '',

    // ── Charges ──
    deliveryCharge:      orderData.deliveryCharge      || 0,
    vendorPackingCharge: orderData.vendorPackingCharge || 0,
    platformFee:         orderData.platformFee         || 0,
    tipAmount:           orderData.tipAmount           || 0,
    discount:            orderData.discount            || 0,
    discountAmountOffer: orderData.discountAmountOffer || 0,
    offerPercentage:     orderData.offerPercentage     || 0,
    itemSubTotal:        orderData.itemSubTotal        || 0,
    subtotal:            orderData.subtotal            || 0,
    tax:                 orderData.tax                 || 0,
    taxRate:             orderData.taxRate             || 0,
    totalAmount:         orderData.totalAmount         || 0,
    rewardPointsEarned:  orderData.rewardPointsEarned  || 0,

    // ── Items ──
    orderDetails: orderData.orderDetails || [],

    // ── Type & Mode ──
    deliveryType: orderData.deliveryType || 'delivery',
    orderMode:    orderData.orderMode    || 'delivery',
    orderType:    orderData.orderType    || 'instant',

    // ── Payment ──
    paymentMethod:     isCOD ? 'COD' : (orderData.paymentMethod || 'Online'),
    razorpayPaymentId: orderData.razorpayPaymentId || '',
    razorpayOrderId:   orderData.razorpayOrderId   || '',
    razorpaySignature: orderData.razorpaySignature || '',

    // ── Status ──
    status:     initialStatus,
    mainStatus: initialMain,

    // ── Status History ──
    // NOTE: serverTimestamp() is NOT allowed inside arrays — use new Date()
    statusHistory: [{
      createdAtHis: new Date(),
      description,
      mainStatus:  initialMain,
      status:      initialStatus,
      updatedBy:   userId,
    }],

    // ── Vendor ──
    vendorId,
    vendorName,
  };

  batch.set(orderRef, orderDoc);

  // Append docId to user's orders array
  const userRef  = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    const prev = userSnap.data().orders || [];
    batch.update(userRef, { orders: [...prev, docId] });
  }

  await batch.commit();
  return { docId, orderId };
};

// Update order status — appends new entry to statusHistory
// NOTE: serverTimestamp() is NOT allowed inside arrays — use new Date()
export const updateOrderStatus = async (docId, status, mainStatus, description, updatedBy = 'system') => {
  const orderRef = doc(db, 'orders', docId);
  const snap     = await getDoc(orderRef);
  if (!snap.exists()) throw new Error('Order not found');
  const history  = snap.data().statusHistory || [];
  await updateDoc(orderRef, {
    status,
    mainStatus,
    updatedAt: serverTimestamp(),
    statusHistory: [
      ...history,
      {
        createdAtHis: new Date(),
        description,
        mainStatus,
        status,
        updatedBy,
      },
    ],
  });
};

export const getOrders = async (userId) => {
  // Query only by userId (avoids composite index requirement).
  // Then filter orderStatus === 'Web' in JS — safe and always works.
  const q = query(
    collection(db, 'orders'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(o => o.orderStatus === 'Web');   // ← client-side filter
};

export const getAllOrders = async () => {
  const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─────────────────────────────────────────
// WISHLIST
// ─────────────────────────────────────────
export const toggleWishlist = async (userId, productId) => {
  const userRef = doc(db, 'users', userId);
  const snap    = await getDoc(userRef);
  if (!snap.exists()) return [];
  const wishlist    = snap.data().wishlist || [];
  const newWishlist = wishlist.includes(productId)
    ? wishlist.filter(id => id !== productId)
    : [...wishlist, productId];
  await updateDoc(userRef, { wishlist: newWishlist });
  return newWishlist;
};

// ─────────────────────────────────────────
// REVIEWS
// ─────────────────────────────────────────
export const addReview = async (vendorId, userId, displayName, rating, comment) => {
  await addDoc(collection(db, 'reviews'), {
    vendorId, userId, displayName, rating, comment,
    createdAt: serverTimestamp(),
  });
};
export const getReviews = async (vendorId) => {
  try {
    const q = query(
      collection(db, 'reviews'),
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
};

// ─────────────────────────────────────────
// IMAGE UPLOAD
// ─────────────────────────────────────────
export const uploadImage = (file, path, onProgress) =>
  new Promise((resolve, reject) => {
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      'state_changed',
      snap => onProgress && onProgress((snap.bytesTransferred / snap.totalBytes) * 100),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });

// ─────────────────────────────────────────
// DASHBOARD STATS
// ─────────────────────────────────────────
export const getDashboardStats = async () => {
  const [orders, vendors, users, items] = await Promise.all([
    getDocs(collection(db, 'orders')),
    getDocs(collection(db, 'vendors')),
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'restaurantitems')),
  ]);
  const orderData = orders.docs.map(d => d.data());
  const revenue   = orderData.reduce((a, o) => a + (o.totalAmount || o.total || 0), 0);
  const pending   = orderData.filter(o =>
    o.status === 'Created' || o.status === 'Accepted' || o.status === 'Ready'
  ).length;
  return {
    totalRevenue:  revenue,
    totalOrders:   orders.size,
    totalVendors:  vendors.size,
    totalUsers:    users.size,
    totalItems:    items.size,
    pendingOrders: pending,
    recentOrders:  orders.docs.slice(0, 5).map(d => ({ id: d.id, ...d.data() })),
  };
};